import {
  Button,
  Content,
  Modal,
  ModalBackground,
  ModalCard, ModalCardBody, ModalCardFooter,
  ModalCardHeader, ModalCardTitle
} from 'bloomer'
import * as React from 'react'
import { BrowserView, isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { IArea, IQuestion } from '../../../api/types/checklist'
import ITeam from '../../../api/types/team'
import IUser from '../../../api/types/user'
import { INotFilledQuestion } from '../../../pages/checklists/ResultView/types'
import RequiredFieldAreaNoPermissionMessage from './RequiredFieldModalPermissionNotification'

interface IProps {
  onClose: () => void
  // question: with area_position (copy of a duplicated area) of the missing answer
  onJumpToQuestion: (question?: IQuestion) => void
  errors: INotFilledQuestion[]
  areas: IArea[]
  isSignedValid?: boolean
  currentUserCanAccessArea: (area: IArea) => boolean
  users: IUser[]
  teams: ITeam[]
  // name of an area copy ("Kühlregal #(2)" or the name given in the process)
  areaTitle?: (area: IArea) => string
}

const copyOf = (area: IArea) => area.area_position ?? 1
const inArea = (error: INotFilledQuestion, area: IArea) => error.area_id === area.id && (error.area_position ?? 1) === copyOf(area)

// ADF-335: this component shows a modal dialog with a list of all not filled required fields when closing a checklist
// the user can jump to the fields by clicking a link
function NotFilledRequiredFieldsModal({
  onClose,
  errors,
  areas,
  onJumpToQuestion,
  isSignedValid,
  currentUserCanAccessArea,
  users,
  teams,
  areaTitle
}: IProps) {
  const { t } = useTranslation()

  const areasWithErrors = []

  // collect a list of all areas (each copy of a duplicated area) that contain not filled required questions
  areas.forEach((area) => {
    if (errors.some((x) => inArea(x, area)) && !areasWithErrors.includes(area)) {
      areasWithErrors.push(area)
    }
  })

  // get the errors for each area.
  // make a list of links to questions
  const shownAreasWithErrors = areasWithErrors.map((area) => {
    const errorsOfThisArea = errors.filter((x) => inArea(x, area))

    const errorItems = errorsOfThisArea.map((error) => {
      const onClickQuestion = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()
        if (currentUserCanAccessArea(area)) {
          onJumpToQuestion(error)
        }
      }

      const questionsLinkClass = !currentUserCanAccessArea(area) ? 'has-not-allowed not-allowed-link' : ''

      return (
        <li key={`error-${error.id}-${error.question_position ?? 1}`}>
          <a href="#" onClick={onClickQuestion} className={questionsLinkClass}>
            { (error.question_position ?? 1) > 1 ? `${error.title} #${error.question_position}` : error.title }
            { error.attachmentMissing ? ` (${t('checklist.attachmentRequired')})` : null }
            { error.deviationCommentMissing ? ` (${t('expectedAnswer.commentRequired')})` : null }
            { error.deviationPhotoMissing ? ` (${t('expectedAnswer.photoRequired')})` : null }
          </a>
        </li>
      )
    })

    const permissionNotification = !currentUserCanAccessArea(area)
      ? <RequiredFieldAreaNoPermissionMessage area={area} users={users} teams={teams} /> : null

    return (
      <div key={`area-${area.id}-${copyOf(area)}`} className="error-modal-item">
        <h4>{ areaTitle ? areaTitle(area) : area.title }</h4>
        { permissionNotification }
        <ul>
          { errorItems }
        </ul>
      </div>
    )
  })

  // special case: Old signature feature. This is not a real area.
  // if onJumpToQuestion is called with null as argument then it will jump to the signatures pseudo area.
  const onClickSignatureQuestion = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    onJumpToQuestion(null)
  }

  // if the signatures are required and not filled then show it as error
  const signatureMissing = !isSignedValid
    ? (
      <div className="error-modal-item">
        <h4>{ t('Signatures') }</h4>
        <ul>
          <li key="error-signature">
            <a href="#" onClick={onClickSignatureQuestion}>{ t('Signature') }</a>
          </li>
        </ul>
      </div>

    ) : null

  // This is a full screen modal on phones and a regular modal on desktop.
  return (
    <Modal
      isActive
      className={
        isMobile
          ? 'is-huge modal-full-screen modal-fx-fadeInScale error-result-modal'
          : 'is-huge modal-fx-fadeInScale error-result-modal'
      }
    >
      <BrowserView>
        <ModalBackground onClick={onClose} />
      </BrowserView>

      <ModalCard className="modal-content" style={{ margin: 0 }}>
        <ModalCardHeader>
          <ModalCardTitle>{ t('checklist.validationFailed') }</ModalCardTitle>
        </ModalCardHeader>
        <ModalCardBody>
          { t('resultSet.resultRequiredAlert') }
          <Content>
            { shownAreasWithErrors }
            { signatureMissing }
          </Content>
        </ModalCardBody>
        <ModalCardFooter>
          <Button onClick={onClose} className="is-link">{ t('Close') }</Button>
        </ModalCardFooter>
      </ModalCard>
    </Modal>
  )
}

export default NotFilledRequiredFieldsModal
